import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Target, Award, Users, Zap, CheckCircle2, ArrowRight, HeartHandshake, Clock, Smile } from 'lucide-react';
import { AnimatedSection, AnimatedGrid, AnimatedGridItem } from '../components/AnimatedSection';
export function About() {
  return <div className="w-full" data-id="element-96">
      {/* HERO SECTION */}
      <section className="relative bg-navy text-white py-24 md:py-32 overflow-hidden" data-id="element-97">
        <div className="absolute inset-0 z-0" data-id="element-98">
          <img src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?q=80&w=2070&auto=format&fit=crop" alt="Electrical engineering plans" className="w-full h-full object-cover opacity-20" data-id="element-99" />
          <div className="absolute inset-0 bg-navy/90 mix-blend-multiply" data-id="element-100"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-101"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center" data-id="element-102">
          <AnimatedSection data-id="element-103">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6 max-w-4xl mx-auto" data-id="element-104">
              Electrical Expertise You Can{' '}
              <span className="text-accent" data-id="element-105">Trust</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto" data-id="element-106">
              At The Electrical Professionals Inc, we are committed to
              delivering safe, dependable, and professional electrical services
              for residential and commercial customers.
            </p>
          </AnimatedSection>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-accent" data-id="element-107"></div>
      </section>

      {/* COMPANY STORY */}
      <section className="py-20 md:py-28 bg-white" data-id="element-108">
        <div className="max-w-7xl mx-auto px-6" data-id="element-109">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-110">
            <AnimatedSection className="order-2 md:order-1 relative" data-id="element-111">
              <div className="aspect-[4/5] rounded-sm overflow-hidden shadow-2xl relative z-10" data-id="element-112">
                <video src="https://cdn.magicpatterns.com/uploads/1VSaidLQ8zJWNpQctUD4w3/kling_20260618_VIDEO__Image1Cre_150_0%20(1).mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-113" />
              </div>
              <div className="absolute top-10 -left-10 w-full h-full border-2 border-accent/20 rounded-sm z-0 hidden md:block" data-id="element-114"></div>
            </AnimatedSection>

            <AnimatedSection className="order-1 md:order-2" data-id="element-115">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy mb-6 tracking-tight" data-id="element-116">
                About The Electrical Professionals Inc
              </h2>
              <div className="w-20 h-1.5 bg-accent mb-8" data-id="element-117"></div>

              <div className="text-lg text-slate-600 leading-relaxed" data-id="element-118">
                <p className="mb-6" data-id="element-119">
                  At The Electrical Professionals Inc, we are committed to
                  delivering safe, dependable, and professional electrical
                  services for residential and commercial customers throughout
                  Central Florida. Our team understands that electrical systems
                  are one of the most important components of any home or
                  business, which is why we approach every project with
                  precision, integrity, and attention to detail.
                </p>
                <p data-id="element-120">
                  From troubleshooting and repairs to panel upgrades,
                  installations, and emergency electrical services, we provide
                  solutions designed to keep your property running safely and
                  efficiently. No matter the size of the project, our goal is to
                  deliver quality workmanship, clear communication, and
                  exceptional customer service from start to finish.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* MISSION & VALUES */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-id="element-121">
        <div className="max-w-7xl mx-auto px-6" data-id="element-122">
          <AnimatedSection className="text-center max-w-4xl mx-auto mb-20" data-id="element-123">
            <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 px-3 py-1.5 rounded-full mb-6" data-id="element-124">
              <Target className="w-4 h-4 text-yellow-400" data-id="element-125" />
              <span className="text-sm font-semibold tracking-wider text-yellow-400 uppercase" data-id="element-126">
                Our Mission
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy leading-tight" data-id="element-127">
              "To provide safe, reliable, and affordable electrical services
              while building lasting customer relationships through
              professionalism and quality workmanship."
            </h2>
          </AnimatedSection>

          <AnimatedSection data-id="element-128">
            <h2 className="font-display text-3xl font-bold text-navy mb-8 tracking-tight text-center" data-id="element-129">
              Our Core Values
            </h2>
            <div className="w-20 h-1.5 bg-accent mx-auto mb-12" data-id="element-130"></div>
            <AnimatedGrid className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-131">
              {[{
              title: 'Safety',
              icon: Shield
            }, {
              title: 'Integrity',
              icon: HeartHandshake
            }, {
              title: 'Reliability',
              icon: Clock
            }, {
              title: 'Professionalism',
              icon: Award
            }, {
              title: 'Customer Satisfaction',
              icon: Smile
            }].map((value, i) => <AnimatedGridItem key={i} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 text-center" data-id="element-132">
                  <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-133">
                    <value.icon className="w-8 h-8 text-accent" data-id="element-134" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-navy" data-id="element-135">
                    {value.title}
                  </h3>
                </AnimatedGridItem>)}
            </AnimatedGrid>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-16 bg-navy text-white" data-id="element-136">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-137">
          <AnimatedSection data-id="element-138">
            <h2 className="font-display text-3xl font-bold mb-2" data-id="element-139">
              Ready to start your project?
            </h2>
            <p className="text-slate-300" data-id="element-140">
              Contact us today for a free, no-obligation estimate.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2} data-id="element-141">
            <Link to="/quote" className="bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-sm font-semibold transition-colors inline-flex items-center gap-2" data-id="element-142">
              Request a Free Quote <ArrowRight className="w-5 h-5" data-id="element-143" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}